import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, CheckCircle, AlertCircle, Linkedin } from 'lucide-react';

// Primary phone number - standardized across site
const PRIMARY_PHONE = '(918) 493-3418';
const PRIMARY_PHONE_TEL = '9184933418';

// External links
const IRONCLAD_URL = 'https://ironclad.williamsdiversified.com/';
const LINKEDIN_URL = 'https://www.linkedin.com/company/williams-diversified/';

// Compact footer contact form
function FooterContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
    website: '', // Honeypot
  });
  const [status, setStatus] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Honeypot check
    if (formData.website) {
      setStatus('success');
      return;
    }
    
    if (!formData.name || !formData.email || !formData.message) {
      setStatus('validation');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          message: formData.message,
          serviceType: 'other',
        }),
      });
      
      if (response.ok) {
        setStatus('success');
        setFormData({ name: '', email: '', message: '', website: '' });
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (status === 'success') {
    return (
      <div className="text-center py-4">
        <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
        <p className="text-white text-sm">Thank you! We&apos;ll be in touch soon.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3" data-testid="footer-contact-form">
      {/* Honeypot */}
      <div className="absolute -left-[9999px]" aria-hidden="true">
        <input
          type="text"
          name="website"
          value={formData.website}
          onChange={(e) => setFormData({ ...formData, website: e.target.value })}
          tabIndex="-1"
          autoComplete="off"
        />
      </div>
      
      <input
        type="text"
        placeholder="Your Name *"
        value={formData.name}
        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
        className="w-full bg-black border border-gold/30 rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-gold"
        data-testid="footer-form-name"
      />
      <input
        type="email"
        placeholder="Your Email *"
        value={formData.email}
        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
        className="w-full bg-black border border-gold/30 rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-gold"
        data-testid="footer-form-email"
      />
      <textarea
        placeholder="Quick Message *"
        value={formData.message}
        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
        rows="2"
        className="w-full bg-black border border-gold/30 rounded px-3 py-2 text-white text-sm focus:outline-none focus:border-gold resize-none"
        data-testid="footer-form-message"
      ></textarea>
      
      {status === 'validation' && (
        <p className="text-red-400 text-xs flex items-center">
          <AlertCircle className="w-3 h-3 mr-1" /> Please fill in all fields
        </p>
      )}
      {status === 'error' && (
        <p className="text-red-400 text-xs flex items-center">
          <AlertCircle className="w-3 h-3 mr-1" /> Error sending. Try again or call us.
        </p>
      )}
      
      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full bg-gold hover:bg-gold-dark text-black font-bold py-2 rounded text-sm transition-all uppercase tracking-wider"
        data-testid="footer-form-submit"
      >
        {isSubmitting ? 'Sending...' : 'Send Message'}
      </button>
    </form>
  );
}

function Footer() {
  return (
    <footer className="bg-black border-t border-gold/30 py-16 relative">
      <div className="absolute inset-0 brass-lighting"></div>
      <div className="absolute inset-0 geometric-brass"></div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Main Footer Content - 4 Columns */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          
          {/* Column 1: Logo + Tagline + Contact Info */}
          <div className="lg:col-span-1">
            <picture>
              <source srcSet="/images/wd_logo_footer.webp" type="image/webp" />
              <img 
                src="/images/wd_logo_footer.png" 
                alt="Williams Diversified" 
                className="h-48 w-auto mb-6"
                width="265"
                height="177"
                loading="lazy"
              />
            </picture>
            <p className="text-gold font-semibold text-sm mb-6 uppercase tracking-wider">
              Your Vision. Our Integrity. Exceptional Results.
            </p>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <Phone className="w-5 h-5 text-gold flex-shrink-0 mt-1" />
                <div>
                  <p className="text-gray-400 text-xs mb-1">24/7 Emergency Response</p>
                  <a href={`tel:${PRIMARY_PHONE_TEL}`} className="text-white hover:text-gold transition-colors">{PRIMARY_PHONE}</a>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <Mail className="w-5 h-5 text-gold flex-shrink-0 mt-1" />
                <div>
                  <p className="text-gray-400 text-xs mb-1">Email</p>
                  <a href="mailto:inquire@williamsdiversified.com" className="text-white hover:text-gold transition-colors break-all">inquire@williamsdiversified.com</a>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-gold flex-shrink-0 mt-1" />
                <div>
                  <p className="text-gray-400 text-xs mb-1">Headquarters</p>
                  <p className="text-white text-sm">1913 W Tacoma St, Suite A &amp; B</p>
                  <p className="text-white text-sm">Broken Arrow, OK 74012</p>
                  <p className="text-gold text-sm mt-1">Nationwide &amp; Worldwide</p>
                </div>
              </div>
            </div>
            
            {/* Social Links */}
            <div className="flex items-center space-x-4 mt-6">
              <a 
                href={LINKEDIN_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gold/20 hover:bg-gold text-gold hover:text-black p-2 rounded transition-all"
                aria-label="Williams Diversified LinkedIn"
                data-testid="footer-linkedin"
              >
                <Linkedin className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Column 2: Quick Links */}
          <div>
            <h3 className="text-white font-bold text-lg mb-6 uppercase tracking-wider">Quick Links</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/" className="text-gray-400 hover:text-gold transition-colors">Home</Link>
              </li>
              <li>
                <Link to="/about" className="text-gray-400 hover:text-gold transition-colors">About Us</Link>
              </li>
              <li>
                <Link to="/services" className="text-gray-400 hover:text-gold transition-colors">Services</Link>
              </li>
              <li>
                <Link to="/industries" className="text-gray-400 hover:text-gold transition-colors">Industries</Link>
              </li>
              <li>
                <Link to="/certifications" className="text-gray-400 hover:text-gold transition-colors">Certifications</Link>
              </li>
              <li>
                <Link to="/testimonials" className="text-gray-400 hover:text-gold transition-colors">Testimonials</Link>
              </li>
              <li>
                <Link to="/careers" className="text-gray-400 hover:text-gold transition-colors">Careers</Link>
              </li>
              <li>
                <Link to="/contact" className="text-gray-400 hover:text-gold transition-colors">Contact</Link>
              </li>
              <li>
                <a 
                  href={IRONCLAD_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gold hover:text-gold-dark transition-colors font-medium"
                  data-testid="footer-employee-portal"
                >
                  Employee Portal
                </a>
              </li>
            </ul>
          </div>

          {/* Column 3: Services & Industries */}
          <div>
            <h3 className="text-white font-bold text-lg mb-6 uppercase tracking-wider">Services &amp; Industries</h3>
            <div className="space-y-4">
              <div>
                <p className="text-gold font-semibold text-sm mb-2">Services</p>
                <ul className="space-y-2">
                  <li>
                    <Link to="/government-contracting" className="text-gray-400 hover:text-gold transition-colors text-sm">Government Contracting</Link>
                  </li>
                  <li>
                    <Link to="/services" className="text-gray-400 hover:text-gold transition-colors text-sm">Private Sector Services</Link>
                  </li>
                  <li>
                    <Link to="/facility-maintenance" className="text-gray-400 hover:text-gold transition-colors text-sm">Facility Maintenance</Link>
                  </li>
                  <li>
                    <Link to="/private-sector/exterior-services" className="text-gray-400 hover:text-gold transition-colors text-sm">Exterior Services</Link>
                  </li>
                </ul>
              </div>
              <div>
                <p className="text-gold font-semibold text-sm mb-2">Industries</p>
                <ul className="space-y-2">
                  <li>
                    <Link to="/industries/government-defense" className="text-gray-400 hover:text-gold transition-colors text-sm">Government &amp; Defense</Link>
                  </li>
                  <li>
                    <Link to="/industries/healthcare" className="text-gray-400 hover:text-gold transition-colors text-sm">Healthcare</Link>
                  </li>
                  <li>
                    <Link to="/industries/commercial-real-estate" className="text-gray-400 hover:text-gold transition-colors text-sm">Commercial Real Estate</Link>
                  </li>
                  <li>
                    <Link to="/industries/financial-institutions" className="text-gray-400 hover:text-gold transition-colors text-sm">Financial Institutions</Link>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Column 4: Quick Contact Form */}
          <div>
            <h3 className="text-white font-bold text-lg mb-6 uppercase tracking-wider">Quick Contact</h3>
            <FooterContactForm />
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gold/30 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-gray-400 text-sm">
              © 2025 Williams Diversified LLC. All rights reserved.
            </div>
            <div className="flex flex-wrap justify-center gap-6">
              <Link to="/privacy-policy" className="text-gray-400 hover:text-gold transition-colors text-sm">Privacy Policy</Link>
              <Link to="/terms-of-service" className="text-gray-400 hover:text-gold transition-colors text-sm">Terms of Service</Link>
              <Link to="/accessibility-statement" className="text-gray-400 hover:text-gold transition-colors text-sm">Accessibility Statement</Link>
              <Link to="/equal-opportunity-employer" className="text-gray-400 hover:text-gold transition-colors text-sm">Equal Opportunity Employer</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
